import { useState, useCallback, useEffect } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  QueryConstraint,
  DocumentData,
  FirestoreError,
  WithFieldValue,
  DocumentReference,
  QuerySnapshot,
  DocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

interface UseFirestoreOptions {
  realtime?: boolean;
}

interface QueryOptions {
  field?: string;
  operator?: string;
  value?: any;
  orderByField?: string;
  orderDir?: 'asc' | 'desc';
  limitCount?: number;
}

interface UseFirestoreReturn<T> {
  // State
  data: T[] | null;
  document: T | null;
  loading: boolean;
  error: string | null;

  // CRUD Operations
  getDocument: (id: string) => Promise<T | null>;
  getDocuments: (options?: QueryOptions) => Promise<T[]>;
  addDocument: (data: WithFieldValue<T>) => Promise<DocumentReference<T>>;
  updateDocument: (id: string, data: Partial<T>) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;

  // Real-time listeners
  subscribeToCollection: (options?: QueryOptions) => () => void;
  subscribeToDocument: (id: string) => () => void;
}

export function useFirestore<T = DocumentData>(
  collectionName: string,
  options: UseFirestoreOptions = {}
): UseFirestoreReturn<T> {
  const { realtime = false } = options;
  
  const [data, setData] = useState<T[] | null>(null);
  const [document, setDocument] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const getCollectionRef = useCallback(() => {
    return collection(db, collectionName);
  }, [collectionName]);

  const getDocumentRef = useCallback((id: string) => {
    return doc(db, collectionName, id);
  }, [collectionName]);

  const buildQuery = useCallback((options?: QueryOptions): QueryConstraint[] => {
    const constraints: QueryConstraint[] = [];
    
    if (options?.field && options?.operator && options?.value !== undefined) {
      constraints.push(where(options.field, options.operator as any, options.value));
    }
    
    if (options?.orderByField) {
      constraints.push(orderBy(options.orderByField, options.orderDir || 'asc'));
    }
    
    if (options?.limitCount) {
      constraints.push(limit(options.limitCount));
    }
    
    return constraints;
  }, []);

  const getDocument = useCallback(async (id: string): Promise<T | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const docRef = getDocumentRef(id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = { id: docSnap.id, ...docSnap.data() } as T;
        setDocument(docData);
        return docData;
      } else {
        setError('Document not found');
        return null;
      }
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error getting document: ${firestoreError.message}`);
      console.error('Error getting document:', firestoreError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getDocumentRef]);

  const getDocuments = useCallback(async (options?: QueryOptions): Promise<T[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const collectionRef = getCollectionRef();
      const queryConstraints = buildQuery(options);
      const q = query(collectionRef, ...queryConstraints);
      const querySnapshot = await getDocs(q);
      
      const documents: T[] = [];
      querySnapshot.forEach((doc) => {
        documents.push({ id: doc.id, ...doc.data() } as T);
      });
      
      setData(documents);
      return documents;
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error getting documents: ${firestoreError.message}`);
      console.error('Error getting documents:', firestoreError);
      return [];
    } finally {
      setLoading(false);
    }
  }, [getCollectionRef, buildQuery]);

  const addDocument = useCallback(async (
    documentData: WithFieldValue<T>
  ): Promise<DocumentReference<T>> => {
    setLoading(true);
    setError(null);
    
    try {
      const collectionRef = getCollectionRef();
      const docRef = await addDoc(collectionRef, documentData as any);
      
      // Update local state
      const newDocument = { id: docRef.id, ...(documentData as any) } as T;
      setData(prev => prev ? [...prev, newDocument] : [newDocument]);
      
      return docRef as DocumentReference<T>;
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error adding document: ${firestoreError.message}`);
      console.error('Error adding document:', firestoreError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getCollectionRef]);

  const updateDocument = useCallback(async (
    id: string,
    updateData: Partial<T>
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const docRef = getDocumentRef(id);
      await updateDoc(docRef, updateData as any);
      
      // Update local state
      setData(prev => {
        if (!prev) return prev;
        return prev.map(doc => 
          (doc as any).id === id ? { ...doc, ...updateData } : doc
        ) as T[];
      });
      
      if (document && (document as any).id === id) {
        setDocument(prev => prev ? { ...prev, ...updateData } as T : prev);
      }
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error updating document: ${firestoreError.message}`);
      console.error('Error updating document:', firestoreError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDocumentRef, document]);

  const deleteDocument = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const docRef = getDocumentRef(id);
      await deleteDoc(docRef);
      
      // Update local state
      setData(prev => prev ? prev.filter(doc => (doc as any).id !== id) : null);
      
      if (document && (document as any).id === id) {
        setDocument(null);
      }
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error deleting document: ${firestoreError.message}`);
      console.error('Error deleting document:', firestoreError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getDocumentRef, document]);

  const subscribeToCollection = useCallback((options?: QueryOptions): (() => void) => {
    setLoading(true);
    setError(null);
    
    try {
      const collectionRef = getCollectionRef();
      const queryConstraints = buildQuery(options);
      const q = query(collectionRef, ...queryConstraints);
      
      const unsubscribe = onSnapshot(
        q,
        (snapshot: QuerySnapshot) => {
          const documents: T[] = [];
          snapshot.forEach((doc) => {
            documents.push({ id: doc.id, ...doc.data() } as T);
          });
          setData(documents);
          setLoading(false);
        },
        (err: FirestoreError) => {
          setError(`Error in real-time subscription: ${err.message}`);
          setLoading(false);
          console.error('Error in real-time subscription:', err);
        }
      );
      
      return unsubscribe;
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error setting up subscription: ${firestoreError.message}`);
      setLoading(false);
      console.error('Error setting up subscription:', firestoreError);
      return () => {}; // Return empty cleanup function
    }
  }, [getCollectionRef, buildQuery]);

  const subscribeToDocument = useCallback((id: string): (() => void) => {
    setLoading(true);
    setError(null);
    
    try {
      const docRef = getDocumentRef(id);
      
      const unsubscribe = onSnapshot(
        docRef,
        (snapshot: DocumentSnapshot) => {
          if (snapshot.exists()) {
            const docData = { id: snapshot.id, ...snapshot.data() } as T;
            setDocument(docData);
          } else {
            setError('Document not found');
            setDocument(null);
          }
          setLoading(false);
        },
        (err: FirestoreError) => {
          setError(`Error in real-time subscription: ${err.message}`);
          setLoading(false);
          console.error('Error in real-time subscription:', err);
        }
      );
      
      return unsubscribe;
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Error setting up subscription: ${firestoreError.message}`);
      setLoading(false);
      console.error('Error setting up subscription:', firestoreError);
      return () => {}; // Return empty cleanup function
    }
  }, [getDocumentRef]);

  // Auto-subscribe if realtime is enabled
  useEffect(() => {
    if (realtime) {
      const unsubscribe = subscribeToCollection();
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [realtime, subscribeToCollection]);

  return {
    // State
    data,
    document,
    loading,
    error,
    
    // CRUD Operations
    getDocument,
    getDocuments,
    addDocument,
    updateDocument,
    deleteDocument,
    
    // Real-time listeners
    subscribeToCollection,
    subscribeToDocument,
  };
}

// Helper hooks for specific collections
export function useUsersCollection() {
  return useFirestore('users');
}

export function useInstitutionsCollection() {
  return useFirestore('institutions');
}

export function useTransactionsCollection() {
  return useFirestore('transactions');
}

export function useWithdrawalsCollection() {
  return useFirestore('withdrawals');
}

export function useSubscriptionsCollection() {
  return useFirestore('subscriptions');
}

// Custom hook for user-specific operations
export function useUserTransactions(userId?: string) {
  const hook = useFirestore('transactions');
  
  const getUserTransactions = useCallback(async () => {
    if (!userId) return [];
    
    return hook.getDocuments({
      field: 'userId',
      operator: '==',
      value: userId,
      orderByField: 'createdAt',
      orderDir: 'desc',
    });
  }, [hook.getDocuments, userId]);
  
  return {
    ...hook,
    getUserTransactions,
  };
}

// Custom hook for institution-specific operations
export function useInstitutionData(institutionId?: string) {
  const hook = useFirestore('institutions');
  
  const getInstitution = useCallback(async () => {
    if (!institutionId) return null;
    
    const institutions = await hook.getDocuments({
      field: 'institutionId',
      operator: '==',
      value: institutionId,
      limitCount: 1,
    });
    
    return institutions[0] || null;
  }, [hook.getDocuments, institutionId]);
  
  return {
    ...hook,
    getInstitution,
  };
}

// Batch operations helper
export const useFirestoreBatch = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeBatch = useCallback(async <T>(
    operations: Array<() => Promise<T>>
  ): Promise<T[]> => {
    setLoading(true);
    setError(null);
    
    try {
      const results = await Promise.all(operations.map(op => op()));
      return results;
    } catch (err) {
      const firestoreError = err as FirestoreError;
      setError(`Batch operation failed: ${firestoreError.message}`);
      console.error('Batch operation failed:', firestoreError);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    executeBatch,
    loading,
    error,
  };
};

// Type-safe query builder
export const createFirestoreQuery = (
  field: string,
  operator: string,
  value: any,
  additionalOptions?: {
    orderByField?: string;
    orderDir?: 'asc' | 'desc';
    limitCount?: number;
  }
): QueryOptions => ({
  field,
  operator,
  value,
  ...additionalOptions,
});

// Helper for pagination
export interface PaginationOptions {
  page: number;
  pageSize: number;
  orderByField: string;
  orderDir?: 'asc' | 'desc';
}

export const usePaginatedCollection = <T = DocumentData>(
  collectionName: string,
  baseQuery?: QueryOptions,
  pagination?: PaginationOptions
) => {
  const [page, setPage] = useState(pagination?.page || 1);
  const [total, ] = useState(0);
  const hook = useFirestore<T>(collectionName);

  const loadPage = useCallback(async () => {
    if (!pagination) {
      return hook.getDocuments(baseQuery);
    }

    // Calculate start point (simplified - Firestore doesn't have offset)
    // In production, you'd use cursor-based pagination
    const queryOptions: QueryOptions = {
      ...baseQuery,
      orderByField: pagination.orderByField,
      orderDir: pagination.orderDir,
      limitCount: pagination.pageSize,
    };

    return hook.getDocuments(queryOptions);
  }, [hook, baseQuery, pagination]);

  useEffect(() => {
    loadPage();
  }, [page, loadPage]);

  const nextPage = () => {
    if (page * (pagination?.pageSize || 10) < total) {
      setPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPage(prev => prev - 1);
    }
  };

  return {
    ...hook,
    page,
    setPage,
    total,
    nextPage,
    prevPage,
    hasNextPage: page * (pagination?.pageSize || 10) < total,
    hasPrevPage: page > 1,
  };
};